import { useEffect, useMemo, useState } from "react";

import { Icon } from "semantic-ui-react";

import { makeStyles } from "@mui/styles";

import moment from "moment";
import { useIntl } from "react-intl";

import { formatDate } from "react-lib/utils/dateUtils";

import SearchBoxInput from "./SearchBoxInput";

type PackageItem = Record<string, any>;

type CardAppointmentPackagesProps = {
  onEvent: (e: any) => any;
  selectedPackages: PackageItem[];
  onChange: (packages: PackageItem[]) => void;
};

type LinkedPackageListProps = {
  title: string;
  packages: PackageItem[];
  // แสดงประเภท รหัส และสถานะการชำระเงิน
  detailed?: boolean;
  showCount?: boolean;
};

// theme color ของ app — "primary-color-first" มีเฉพาะ palette ของ ishealth-main (MeHealth)
// platform ไม่ได้ define ตัวนี้ไว้ → ถ้าไม่ใส่ fallback จะได้พื้นช่องเลือกเป็นสีขาว
// และเครื่องหมายถูกสีขาวจะทับบนขาว = มองไม่เห็นเหมือนไม่ได้เลือก
const THEME_COLORS = {
  first: "var(--primary-color-first, var(--primary-theme-color, #EE5C7E))",
  pinkBorder: "var(--mh-pink-border, #F8B4C4)",
};

const COLORS = {
  grey: "#3C3C3C",
  muted: "#8A8A8A",
  border: "#E5E5E5",
  teal: "#2A9AA6",
  tealLight: "#EEF8F9",
  link: "#2B6CB0",
  paid: "#2E7D32",
  counter: "#B7791F",
  linked: "#FDEEF2",
};

// แสดงรายการแรกก่อนกด View all
const PREVIEW_COUNT = 3;

const STATUS_LABELS: Record<string, string> = {
  PAID: "ชำระแล้ว",
  BILLED_COUNTER: "รอชำระเงินหน้าเคาน์เตอร์",
};

const useStyles = makeStyles(() => ({
  card: {
    borderRadius: "5px",
    padding: "12px 14px",
    color: COLORS.grey,
    marginTop: "12px",
    border: "1px solid rgba(217, 217, 217, 1)",
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: "8px",
  },
  muted: {
    color: COLORS.muted,
    fontSize: "13px",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  search: { margin: "8px 0" },
  checkbox: {
    flexShrink: 0,
    marginTop: "4px",
    width: "22px",
    height: "22px",
    borderRadius: "6px",
    border: `1.5px solid ${THEME_COLORS.pinkBorder}`,
    background: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    "& i.icon": {
      margin: 0,
      padding: 0,
      fontSize: "12px",
      lineHeight: 1,
      color: "#FFFFFF",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "1em",
      height: "1em",
      "&::before": { display: "block", lineHeight: 1 },
    },
  },
  checkboxChecked: {
    background: THEME_COLORS.first,
    borderColor: THEME_COLORS.first,
  },
  item: {
    display: "flex",
    gap: "10px",
    padding: "8px 0",
    borderTop: `1px solid ${COLORS.border}`,
  },
  name: { fontWeight: "bold", fontSize: "14px" },
  meta: {
    fontSize: "12px",
    color: COLORS.muted,
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
  },
  tag: {
    fontSize: "11px",
    fontWeight: "bold",
    borderRadius: "4px",
    padding: "0 6px",
  },
  link: {
    color: COLORS.link,
    fontWeight: "bold",
    fontSize: "13px",
    margin: "6px 0",
  },
  addButton: {
    background: COLORS.tealLight,
    color: COLORS.teal,
    border: `1px solid ${COLORS.teal}`,
    borderRadius: "6px",
    padding: "6px 12px",
    fontWeight: "bold",
    fontSize: "13px",
  },
  catalog: {
    background: COLORS.tealLight,
    border: `1px solid ${COLORS.border}`,
    borderRadius: "8px",
    padding: "10px",
    marginTop: "8px",
  },
  catalogList: { maxHeight: "260px", overflowY: "auto" },
  catalogItem: {
    background: "white",
    border: `1px solid ${COLORS.border}`,
    borderRadius: "6px",
    padding: "8px 10px",
    marginBottom: "6px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
  },
  selectButton: {
    whiteSpace: "nowrap",
    flexShrink: 0,
    border: "none",
    borderRadius: "6px",
    padding: "6px 14px",
    fontWeight: "bold",
    color: "white",
    background: COLORS.teal,
    "&:disabled": { background: "#E0E0E0", color: COLORS.muted },
  },
  linkedItem: {
    background: COLORS.linked,
    borderRadius: "6px",
    padding: "8px 10px",
    marginTop: "6px",
  },
  empty: {
    border: `1px dashed ${COLORS.border}`,
    borderRadius: "6px",
    padding: "10px",
    textAlign: "center",
    color: COLORS.muted,
    fontSize: "13px",
    marginTop: "6px",
  },
}));

export const formatPackagePrice = (price: number | string) =>
  `฿${(Number(price) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// รูปแบบ name_EN (name_TH) ของ package order และแพ็กเกจในระบบ
const getPackageName = (item: PackageItem) => {
  const name = item.product_name ?? item.name;
  const nameEn = item.product_name_en ?? item.name_en;

  return nameEn && nameEn !== name
    ? `${nameEn} (${name})`
    : name || nameEn || "";
};

const getPackageCode = (item: PackageItem) => item.product_code ?? item.code;

const getPaymentStatus = (item: PackageItem) =>
  item.status === "PAID" ? "PAID" : item.custom_status;

const PackageTypeTag = (props: { code?: string }) => {
  const classes = useStyles();
  const intl = useIntl();
  const isLab = props.code === "lab";

  return (
    <span
      className={classes.tag}
      style={
        isLab
          ? { color: COLORS.link, background: "#E8F0FB" }
          : { color: COLORS.paid, background: "#E8F5E9" }
      }
    >
      {intl.formatMessage({ id: isLab ? "แล็บ" : "แพ็กเกจ" })}
    </span>
  );
};

const PackageMeta = (props: { item: PackageItem; showBought?: boolean }) => {
  const classes = useStyles();
  const intl = useIntl();
  const status = getPaymentStatus(props.item);
  const boughtAt = props.item.paid_at || props.item.created_at;

  return (
    <div className={classes.meta}>
      <PackageTypeTag code={props.item.package_type_code} />
      <span>· {getPackageCode(props.item)}</span>
      {props.showBought && boughtAt && (
        <span>
          · {intl.formatMessage({ id: "ซื้อเมื่อ" })}{" "}
          {formatDate(moment(boughtAt))}
        </span>
      )}
      {STATUS_LABELS[status] && (
        <span
          style={{ color: status === "PAID" ? COLORS.paid : COLORS.counter }}
        >
          · {intl.formatMessage({ id: STATUS_LABELS[status] })}
        </span>
      )}
    </div>
  );
};

export const LinkedPackageList = (props: LinkedPackageListProps) => {
  const classes = useStyles();
  const intl = useIntl();

  return (
    <div>
      <div
        className={classes.row}
        style={{ fontWeight: "bold", marginTop: "8px" }}
      >
        <span>{props.title}</span>
        {props.showCount && (
          <span className={classes.muted}>
            {props.packages.length} {intl.formatMessage({ id: "หน่วยรายการ" })}
          </span>
        )}
      </div>
      {props.packages.length ? (
        props.packages.map((item) => (
          <div
            key={item.id}
            className={props.detailed ? classes.item : classes.linkedItem}
            style={props.detailed ? { display: "block" } : undefined}
          >
            <div className={classes.row}>
              <span className={classes.name}>{getPackageName(item)}</span>
              {item.price !== undefined && (
                <span>{formatPackagePrice(item.price)}</span>
              )}
            </div>
            {props.detailed && <PackageMeta item={item} />}
          </div>
        ))
      ) : (
        <div className={classes.empty}>
          {intl.formatMessage({ id: "ไม่มีรายการที่ผูกกับนัดหมายนี้" })}
        </div>
      )}
    </div>
  );
};

const CardAppointmentPackages = (props: CardAppointmentPackagesProps) => {
  const classes = useStyles();
  const intl = useIntl();

  const [options, setOptions] = useState<PackageItem[]>([]);
  const [searchText, setSearchText] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogSearchText, setCatalogSearchText] = useState("");
  const [catalogKeyword, setCatalogKeyword] = useState("");
  const [catalog, setCatalog] = useState<PackageItem[]>([]);
  const [addingId, setAddingId] = useState<number | null>(null);
  const [error, setError] = useState(false);

  const loadOptions = async (): Promise<PackageItem[]> => {
    const result =
      (await props.onEvent({
        message: "GetAppointmentPackageOptions",
        params: {},
      })) || [];

    setOptions(result);

    return result;
  };

  useEffect(() => {
    loadOptions();
  }, []);

  useEffect(() => {
    if (!catalogOpen) {
      return;
    }

    const loadCatalog = async () => {
      const result = await props.onEvent({
        message: "GetAppointmentPackageCatalog",
        params: { keyword: catalogKeyword },
      });

      // ผลค้นหาเดิมที่ถูกยกเลิกจะได้ null
      if (result) {
        setCatalog(result);
      }
    };

    loadCatalog();
  }, [catalogOpen, catalogKeyword]);

  const selectedIds = useMemo(
    () => new Set(props.selectedPackages.map((item) => item.id)),
    [props.selectedPackages],
  );

  const ownedProducts = useMemo(
    () => new Set(options.map((item) => item.product)),
    [options],
  );

  const filteredOptions = useMemo(() => {
    const text = searchText.trim().toLowerCase();

    return options.filter((item) =>
      [item.product_name, item.product_name_en, item.product_code].some(
        (value) => (value || "").toLowerCase().includes(text),
      ),
    );
  }, [options, searchText]);

  const visibleOptions = expanded
    ? filteredOptions
    : filteredOptions.slice(0, PREVIEW_COUNT);

  const handleToggle = (item: PackageItem) => {
    props.onChange(
      selectedIds.has(item.id)
        ? props.selectedPackages.filter((selected) => selected.id !== item.id)
        : [...props.selectedPackages, item],
    );
  };

  // แพ็กเกจที่ยังไม่ได้ซื้อ สร้างเป็นรอชำระเงินหน้าเคาน์เตอร์ แล้วเลือกให้อัตโนมัติ
  const handleSelectCatalog = async (packageId: number) => {
    setAddingId(packageId);
    setError(false);

    const result = await props.onEvent({
      message: "HandleAddAppointmentPackage",
      params: { packageId },
    });

    const latestOptions = result?.orderId ? await loadOptions() : [];
    const added = latestOptions.find((item) => item.id === result?.orderId);

    if (added) {
      props.onChange([...props.selectedPackages, added]);
    } else {
      setError(true);
    }

    setAddingId(null);
  };

  return (
    <div className={classes.card}>
      <div className={classes.row} style={{ fontWeight: "bold" }}>
        <span>
          {intl.formatMessage({ id: "แพ็กเกจ / รายการตรวจสำหรับนัดหมายนี้" })}
        </span>
        <span className={classes.muted} style={{ fontWeight: "normal" }}>
          {intl.formatMessage({ id: "เลือกแล้ว" })}{" "}
          {props.selectedPackages.length}{" "}
          {intl.formatMessage({ id: "หน่วยรายการ" })}
        </span>
      </div>

      <div className={classes.row} style={{ marginTop: "6px" }}>
        <span>{intl.formatMessage({ id: "แพ็กเกจของผู้รับบริการ" })}</span>
        <span className={classes.muted}>
          {options.length} {intl.formatMessage({ id: "หน่วยรายการ" })}
        </span>
      </div>

      <div className={classes.search}>
        <SearchBoxInput
          value={searchText}
          placeholder={intl.formatMessage({ id: "ค้นหาแพ็กเกจของคุณ" })}
          autoFocus={false}
          onChange={setSearchText}
          onDebouncedChange={setSearchText}
        />
      </div>

      {visibleOptions.map((item) => (
        <div key={item.id} className={classes.item}>
          <span
            role="checkbox"
            aria-checked={selectedIds.has(item.id)}
            className={`${classes.checkbox} ${
              selectedIds.has(item.id) ? classes.checkboxChecked : ""
            }`}
            // inline style กันไว้ชั้นหนึ่ง (JSS class order ไม่แน่นอน — ถ้า checkboxChecked
            // ถูกใส่ก่อน checkbox จะทับกันไม่ได้ สีเลือกหาย)
            style={
              selectedIds.has(item.id)
                ? {
                    background: THEME_COLORS.first,
                    borderColor: THEME_COLORS.first,
                  }
                : undefined
            }
            onClick={() => handleToggle(item)}
          >
            {selectedIds.has(item.id) && <Icon name="check" />}
          </span>
          <div style={{ flex: 1 }}>
            <div className={classes.row}>
              <span className={classes.name}>{getPackageName(item)}</span>
              <span>{formatPackagePrice(item.price)}</span>
            </div>
            <PackageMeta item={item} showBought />
          </div>
        </div>
      ))}

      {!filteredOptions.length && (
        <div className={classes.empty}>
          {intl.formatMessage({ id: "ไม่พบแพ็กเกจ" })}
        </div>
      )}

      {filteredOptions.length > PREVIEW_COUNT && (
        <div className={classes.link} onClick={() => setExpanded(!expanded)}>
          {expanded
            ? intl.formatMessage({ id: "ย่อ" })
            : `${intl.formatMessage({ id: "ดูแพ็กเกจทั้งหมด" })} (${
                filteredOptions.length
              } ${intl.formatMessage({ id: "หน่วยรายการ" })})`}
        </div>
      )}

      {catalogOpen ? (
        <div className={classes.catalog}>
          <div className={classes.row}>
            <span style={{ color: COLORS.teal, fontWeight: "bold" }}>
              {intl.formatMessage({ id: "เลือกแพ็กเกจ / รายการตรวจอื่น" })}
            </span>
            <span
              className={classes.muted}
              onClick={() => setCatalogOpen(false)}
            >
              {intl.formatMessage({ id: "ปิด" })}
            </span>
          </div>
          <div className={classes.search}>
            <SearchBoxInput
              value={catalogSearchText}
              placeholder={intl.formatMessage({
                id: "ค้นหาด้วยชื่อแพ็กเกจหรือรหัสรายการตรวจ",
              })}
              debounceMs={400}
              onChange={setCatalogSearchText}
              onDebouncedChange={setCatalogKeyword}
            />
          </div>
          <div className={classes.catalogList}>
            {catalog.map((item) => {
              const isOwned = ownedProducts.has(item.id);

              return (
                <div key={item.id} className={classes.catalogItem}>
                  <div>
                    <div className={classes.name}>{getPackageName(item)}</div>
                    <PackageTypeTag code={item.package_type_code} />
                    <div className={classes.muted}>
                      {getPackageCode(item)} {formatPackagePrice(item.price)}
                    </div>
                  </div>
                  <button
                    type="button"
                    className={classes.selectButton}
                    disabled={isOwned || addingId !== null}
                    onClick={() => handleSelectCatalog(item.id)}
                  >
                    {addingId === item.id ? (
                      <Icon name="spinner" loading style={{ margin: 0 }} />
                    ) : (
                      intl.formatMessage({ id: isOwned ? "มีแล้ว" : "เลือก" })
                    )}
                  </button>
                </div>
              );
            })}
          </div>
          {error && (
            <div style={{ color: "red", fontSize: "13px" }}>
              {intl.formatMessage({ id: "เพิ่มแพ็กเกจไม่สำเร็จ" })}
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          className={classes.addButton}
          onClick={() => setCatalogOpen(true)}
        >
          + {intl.formatMessage({ id: "เลือกแพ็กเกจ / รายการตรวจอื่น" })}
        </button>
      )}

      <LinkedPackageList
        title={intl.formatMessage({ id: "รายการที่ผูกกับนัดหมายนี้" })}
        packages={props.selectedPackages}
      />
    </div>
  );
};

export default CardAppointmentPackages;
